/**
 * ArtifactSettings — per-conversation branding toggles for generated artifacts.
 *
 * An icon trigger in the composer (rendered only when the agent can produce
 * pptx/docx AND the tenant has a live brand book) that opens the same popover
 * the model picker uses — deliberately: this control should feel like a
 * sibling of the model switch, not a new UI idiom. One switch per available
 * format, ON by default; switching one off makes that format render in the
 * plain unbranded design.
 *
 * Styling: reuses the .model-selector* classes so the two popovers can never
 * drift apart visually; ArtifactSettings.css adds only the switch rows.
 */

import { useState, useRef, useEffect, memo } from 'react';
import { Switch } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import './ModelSelector.css';
import './ArtifactSettings.css';

export type BrandableFormat = 'pptx' | 'docx';

interface ArtifactSettingsProps {
  /** Which brandable formats this agent has enabled, in display order. */
  formats: BrandableFormat[];
  /** Override map — an ABSENT key means branded (the default is on). */
  branding: Record<string, boolean>;
  onToggle: (format: BrandableFormat, branded: boolean) => void;
  disabled?: boolean;
}

const FORMAT_LABEL_KEYS: Record<BrandableFormat, string> = {
  pptx: 'chat.artifactBranding.presentations',
  docx: 'chat.artifactBranding.documents',
};

// The dead half of `t(key) || fallback` — never rendered while the keys exist.
const FORMAT_FALLBACK_LABELS: Record<BrandableFormat, string> = {
  pptx: 'Branded presentations',
  docx: 'Branded Word documents',
};

function ArtifactSettingsImpl({
  formats,
  branding,
  onToggle,
  disabled = false,
}: ArtifactSettingsProps) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (formats.length === 0) return null;

  return (
    <div className="model-selector artifact-settings" ref={containerRef}>
      <button
        className={`model-selector-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(o => !o)}
        disabled={disabled}
        type="button"
        aria-label={t('chat.artifactBranding.trigger') || 'Artifact settings'}
        aria-expanded={isOpen}
        title={t('chat.artifactBranding.trigger') || 'Artifact settings'}
      >
        {/* An artifact: a page with a folded corner and content lines. */}
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M14 3v5h5" />
          <path d="M9 13h6" />
          <path d="M9 17h4" />
        </svg>
      </button>

      {isOpen && (
        <div className="model-selector-popover" role="group"
             aria-label={t('chat.artifactBranding.label') || 'Branding'}>
          <div className="model-selector-popover-label">
            {t('chat.artifactBranding.label') || 'Branding'}
          </div>
          {formats.map(fmt => {
            // Absent key = branded. Only an explicit false turns it off.
            const branded = branding[fmt] !== false;
            return (
              <div key={fmt} className="artifact-settings-row">
                <span className="artifact-settings-row-label">
                  {t(FORMAT_LABEL_KEYS[fmt]) || FORMAT_FALLBACK_LABELS[fmt]}
                </span>
                <Switch
                  size="small"
                  checked={branded}
                  onChange={(on) => onToggle(fmt, on)}
                  aria-label={t(FORMAT_LABEL_KEYS[fmt]) || FORMAT_FALLBACK_LABELS[fmt]}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Memoized for the same reason as ModelSelector: the composer re-renders on
 * every keystroke and this popover's props don't change while typing. */
export const ArtifactSettings = memo(ArtifactSettingsImpl);
